import { useEffect, useMemo, useState } from "react";
import { useRoute } from "wouter";
import { Loader2, UserPlus, Calendar, MapPin } from "lucide-react";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useAppNavigation, useIsSignedIn } from "@/lib/platform";
import {
  useInvitePreview,
  useAcceptBookingInvite,
  useReservations,
} from "@/lib/api";
import { setInviteCode, clearInviteCode } from "@/lib/referral";
import { parseUTCDate, formatTimeToDisplay } from "@/utils/dateUtils";
import { getEventTypeLabel, getEventTypeNoun } from "@/utils/eventTypeUtils";
import { useToast } from "@/components/ux/Toast";
import tracking from "@/lib/tracking";
import { useT } from "@/lib/i18n";

/**
 * Landing page for a +1 invite link.
 *
 * Public preview is shown to everyone. The accept action requires sign-in;
 * for unauthenticated users we stash the code in localStorage so the standard
 * sign-up / quiz / payment flow can pick up where it left off and bring them
 * back here.
 */
export default function InviteAccept() {
  const [, params] = useRoute("/invite/:code");
  const code = params?.code || null;

  const { t } = useT();
  const { isLoaded, openSignIn } = useAuth();
  const isSignedIn = useIsSignedIn();
  const { navigate } = useAppNavigation();
  const toast = useToast();

  const { data, isLoading, error } = useInvitePreview(code);
  const { data: reservationsData } = useReservations();
  const accept = useAcceptBookingInvite();
  const [isAccepting, setIsAccepting] = useState(false);

  // Decide upfront whether the user already has an active booking for the
  // invite's event. This drives both the button label and the click handler:
  //   - has booking → server accept call just links the two
  //   - no booking  → bounce into the regular /booking flow (preserves the
  //                   invite code in localStorage so it auto-finalizes after
  //                   the booking activates)
  const inviteEventId = data?.data?.event.id;
  const existingBookingForEvent = useMemo(() => {
    if (!inviteEventId || !reservationsData?.data) return null;
    return (
      reservationsData.data.find(
        (r) =>
          r.eventTime.id === inviteEventId &&
          r.status === "active",
      ) || null
    );
  }, [inviteEventId, reservationsData?.data]);

  // Stash the code so the post-signup / post-payment flow can return here.
  // Accepting is always available — only the inviter-side "send" surface is
  // feature-flagged. People with an in-flight link should always be able to
  // redeem it.
  useEffect(() => {
    if (code) setInviteCode(code);
  }, [code]);

  // Fire a preview-viewed event once the preview resolves (or fails). This is
  // the most valuable signal in the funnel — it captures pre-auth visits the
  // server can't otherwise attribute to the invite.
  const previewStatus = data?.data?.status ?? null;
  const previewError = error || (data && !data.success);
  useEffect(() => {
    if (!code) return;
    if (isLoading) return;
    tracking.invitePreviewViewed(
      code,
      previewError ? "not_found" : previewStatus,
      isSignedIn,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, isLoading, previewStatus, !!previewError]);

  const handleAccept = async () => {
    if (!code) return;
    tracking.inviteAcceptClicked(code, isSignedIn);
    if (!isSignedIn) {
      openSignIn({ onSuccess: () => navigate(`/invite/${code}`), returnTo: `/invite/${code}` });
      return;
    }

    // No active booking yet — bounce into the regular booking flow. The
    // stashed invite code in localStorage gets picked up by BookingContext
    // and accept is re-issued automatically once the booking activates.
    if (!existingBookingForEvent) {
      tracking.inviteAcceptResult(code, "needs_payment");
      const preview = data?.data;
      const qs = new URLSearchParams();
      if (preview?.event.id) qs.set("eventTimeId", preview.event.id);
      if (preview?.neighborhood.id)
        qs.set("neighborhoodId", preview.neighborhood.id);
      if (preview?.neighborhood.cityId)
        qs.set("city", preview.neighborhood.cityId);
      qs.set("inviteCode", code);
      navigate(`/booking?${qs.toString()}`);
      return;
    }

    setIsAccepting(true);
    try {
      const res = await accept.mutateAsync(code);
      if (!res.success || !res.data) {
        tracking.inviteAcceptResult(code, "error");
        toast.error(res.error || t("invites.accept.failedToAccept"));
        setIsAccepting(false);
        return;
      }
      if (res.data.requiresBooking) {
        // Race condition fallback: reservation list said yes, server said no.
        // Send them through the booking flow.
        tracking.inviteAcceptResult(code, "needs_payment");
        const qs = new URLSearchParams();
        if (res.data.eventTimeId) qs.set("eventTimeId", res.data.eventTimeId);
        if (res.data.neighborhoodId)
          qs.set("neighborhoodId", res.data.neighborhoodId);
        if (res.data.cityId) qs.set("city", res.data.cityId);
        qs.set("inviteCode", code);
        navigate(`/booking?${qs.toString()}`);
        return;
      }
      tracking.inviteAcceptResult(code, "accepted");
      clearInviteCode();
      toast.success(
        t("invites.accept.successToast", {
          eventNoun: getEventTypeNoun(data?.data?.event.type),
        }),
      );
      if (res.data.booking?.id) {
        navigate(`/booking/${res.data.booking.id}`);
      } else {
        navigate("/connections");
      }
    } catch (e: any) {
      tracking.inviteAcceptResult(code, "error");
      toast.error(e?.message || t("invites.accept.failedToAccept"));
    } finally {
      setIsAccepting(false);
    }
  };

  if (!code) {
    return (
      <div className="max-w-md mx-auto p-6">
        <p className="text-dark-muted">{t("invites.accept.noCode")}</p>
      </div>
    );
  }

  if (!isLoaded || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (error || !data?.success || !data.data) {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <h1 className="text-2xl font-bold mb-2">
          {t("invites.accept.notFoundTitle")}
        </h1>
        <p className="text-dark-muted">
          {t("invites.accept.notFoundDescription")}
        </p>
      </div>
    );
  }

  const preview = data.data;
  const parsed = parseUTCDate(preview.event.date);
  const isStillPending =
    preview.status === "pending" || preview.status === "accepted";
  const eventNoun = getEventTypeNoun(preview.event.type);
  const eventLabel = getEventTypeLabel(preview.event.type);

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="card text-center py-10">
        <div className="w-14 h-14 rounded-full bg-primary-500/15 flex items-center justify-center mx-auto mb-4">
          <UserPlus className="w-7 h-7 text-primary-500" />
        </div>
        <h1 className="text-2xl font-bold mb-2">
          {t("invites.accept.invitedYou", {
            name: preview.inviter.firstName,
          })}
        </h1>
        <p className="text-dark-muted mb-6">
          {t("invites.accept.joinForEvent", { eventNoun })}
        </p>

        <div className="space-y-3 text-left bg-base-200/50 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-primary-500 shrink-0" />
            <div className="text-sm">
              <div className="font-medium capitalize">
                {parsed.weekday}, {parsed.monthShort} {parsed.day}
              </div>
              <div className="text-dark-muted">
                {formatTimeToDisplay(preview.event.time)} · {eventLabel}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <MapPin className="w-4 h-4 text-primary-500 shrink-0" />
            <div className="text-sm">
              <div className="capitalize">{preview.neighborhood.name}</div>
              {preview.neighborhood.cityName && (
                <div className="text-xs text-dark-muted">
                  {preview.neighborhood.cityName}
                </div>
              )}
            </div>
          </div>
        </div>

        {!isStillPending && (
          <p className="text-sm text-amber-400 mb-4">
            {t("invites.accept.noLongerActive")}
          </p>
        )}

        {!isSignedIn ? (
          // Two CTAs for the unauth flow: brand-new users go through /quiz
          // (the invite code is already stashed in localStorage from this
          // page's mount effect, so the dashboard banner + BookingContext
          // auto-finalize picks it up once they're signed in). Returning
          // users use Sign in.
          <div className="flex flex-col gap-2">
            <button
              onClick={() => {
                if (!code) return;
                tracking.inviteAcceptClicked(code, false);
                // Keep the code stashed — set explicitly in case the user
                // signed out mid-flow and the mount effect already ran.
                setInviteCode(code);
                navigate("/onboarding");
              }}
              disabled={!isStillPending}
              className="btn btn-primary w-full"
            >
              {t("invites.accept.signUpToJoin")}
            </button>
            <button
              onClick={() => {
                if (!code) return;
                tracking.inviteAcceptClicked(code, false);
                openSignIn({ onSuccess: () => navigate(`/invite/${code}`), returnTo: `/invite/${code}` });
              }}
              disabled={!isStillPending}
              className="btn btn-ghost w-full"
            >
              {t("invites.accept.signInExisting")}
            </button>
            <p className="text-xs text-dark-muted mt-1">
              {t("invites.accept.newUserHint")}
            </p>
          </div>
        ) : (
          <button
            onClick={handleAccept}
            disabled={!isStillPending || isAccepting || accept.isPending}
            className="btn btn-primary w-full"
          >
            {isAccepting || accept.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {t("invites.accept.joining")}
              </>
            ) : existingBookingForEvent ? (
              t("invites.accept.acceptInvite")
            ) : (
              t("invites.accept.bookAndJoin", { eventLabel })
            )}
          </button>
        )}
      </div>
    </div>
  );
}

